/**
 * API Client for the Examination System backend.
 *
 * v4 notes:
 * - The nexus-auth SDK owns all auth state (tokens in memory, cookies
 *   handled by the browser). We use the SDK's `attachAuth` helper to
 *   layer Authorization + X-CSRF-Token injection on top of an axios
 *   instance that targets the exam backend.
 * - No localStorage reads. No manual token refresh. No X-User-Id /
 *   X-Device-Id headers. The SDK refreshes proactively in the
 *   background; when a 401 does escape, we delegate to the SDK's
 *   `login()` action, which redirects to nexus-auth.
 * - Error handling for 403/5xx is unchanged — those still drive the
 *   global error store in `@/store/errorStore`.
 */
import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { attachAuth } from '@azyntor-labs/nexus-auth';
import { ApiError } from '@/types';
import { useErrorStore } from '@/store/errorStore';
import { NEXUS_AUTH_CONFIG } from '@/config/nexusAuth';

// Exam backend API URL
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8080/api/v1';

// Helper to set errors in store
const setApiError = (type: 'forbidden' | 'server_error', status?: number, message?: string) => {
  const store = useErrorStore.getState();
  if (type === 'forbidden') {
    store.setForbidden(message);
  } else if (type === 'server_error') {
    store.setServerError(status, message);
  }
};

export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

// Layer the SDK's auth interceptors on top of our exam backend axios
// instance. This attaches `Authorization: Bearer <accessToken>` to every
// request and `X-CSRF-Token` to every state-changing verb. It does NOT
// touch localStorage and never mutates the request body.
attachAuth(apiClient);

// Auth failure tracking (kept to avoid cascading 401 redirects while a
// single bad request is still in flight).
let authFailed = false;
let isRedirectingToLogin = false;

/**
 * Redirect the user back to the nexus-auth login page. The SDK's store
 * clears itself automatically before the redirect via config watchers,
 * so we only need to build the redirect URL.
 */
const handleSSOLogout = () => {
  if (isRedirectingToLogin) return;
  isRedirectingToLogin = true;
  const returnUrl = window.location.href;
  setTimeout(() => {
    NEXUS_AUTH_CONFIG.redirectToLogin(returnUrl);
  }, 100);
};

export const isAuthFailed = () => authFailed;
export const resetAuthFailed = () => {
  authFailed = false;
};

// Legacy exports (no-op now - the SDK owns the refresh lifecycle).
export const startTokenRefreshCheck = () => {};
export const stopTokenRefreshCheck = () => {};
export const initializeAuth = () => {};
export const cleanupAuth = () => {};

// Response interceptor for error handling (403, 5xx).
// 401 is handled entirely by the SDK's attachAuth() interceptor above —
// it does refresh-then-retry in one shot. We do NOT add our own 401
// handler here because that causes a "retry storm" with duplicate
// refreshes and 500ms sleeps that add up to 3-5 seconds on cold start.
apiClient.interceptors.response.use(
  (response) => {
    authFailed = false;
    return response;
  },
  async (error: AxiosError<ApiError>) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _skipErrorHandler?: boolean;
    };
    const status = error.response?.status;

    if (authFailed || originalRequest._skipErrorHandler) {
      return Promise.reject(error);
    }

    if (status === 403) {
      const message = error.response?.data?.message || 'You do not have permission to access this resource';
      setApiError('forbidden', 403, message);
      return Promise.reject(error);
    }

    if (status && status >= 500) {
      const message = error.response?.data?.message || 'A server error occurred';
      setApiError('server_error', status, message);
      return Promise.reject(error);
    }

    // 401 after the SDK's interceptor already tried to refresh — session
    // is genuinely gone. Redirect to login once (not per-request).
    if (status === 401 && !authFailed) {
      authFailed = true;
      handleSSOLogout();
    }

    return Promise.reject(error);
  },
);

export default apiClient;
